/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { reduxForm, reset, submit } from 'redux-form';
import { useHistory, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { useInjectSaga } from '../../utils/injectSaga';
import { useInjectReducer } from '../../utils/injectReducer';
import saga from './saga';
import reducer from './reducer';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import {
	getOrderNameSelector,
	getIsOrderAddFormOpenSelector,
	getIsOrderEditFormOpenSelector,
	getIsModalShownSelector,
	getActiveOrderIdSelector,
	getIsOrderInfoLoadingSelector,
	getIsAuditTabShownSelector,
	getFormInitialValuesSelector,
	getIsOrderInfoSendingSelector,
	getOrderTabsSelector,
	getOrderActionsSelector,
} from './selectors';
import {
	createOrderAction,
	deleteOrderAction,
	getOrderInfoAction,
	resetStateAction,
	updateOrderAction,
} from './actions';
import { OrdersInfo } from './components/order-info';
import { FORM_NAME } from './constants';
import { getIsAvailableOrdersButtonsSelector } from '../profile/selectors';
import { setWindowInfoAction } from '../alert-modal/actions';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { Audit } from '../../components/audit';
import { OrderFormContainer } from './components/order-form';
import closeModal from '../../utils/closeModal';
import { validate } from './utils';

const OrdersModalWrapper = ({
	isModalShown,
	activeOrderId,
	isOrderEditFormOpen,
	isOrderAddFormOpen,
	orderName,
	onResetState,
	onDeleteOrder,
	onCreateOrder,
	onUpdateOrder,
	onGetOrderInfo,
	onFormSubmit,
	isAvailable,
	isOrderInfoLoading,
	onSetWindowInfo,
	isAuditTabShown,
	isOrderInfoSending,
	onClearForm,
	orderTabs,
	orderActions,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'ordersModalSaga', saga });
	useInjectReducer({ key: 'ordersModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		if (isModalShown && activeOrderId) {
			onGetOrderInfo(activeOrderId);
		}
	}, [isModalShown, activeOrderId]);

	const handleOrderFormOpenButtonClick = mode => {
		if (activeOrderId) {
			query.set('id', activeOrderId);
		} else {
			query.delete('id');
		}

		if (mode === 'edit') {
			query.set('mode', 'edit');
		}

		if (mode === 'cancel') {
			onClearForm();
			query.delete('mode');
		}

		if (mode === 'cancel' && !activeOrderId) {
			onResetState();
			onClearForm();
			query.delete('modal');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		closeModal(query);

		onResetState();
		onClearForm();
		history.push(`${pathname}?${query}`);
	};

	const handleNavItemClick = tab => {
		if (tab === 'info') {
			query.delete('tab');
		}

		if (tab === 'audit') {
			query.set('tab', 'audit');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleOrderDelete = () => {
		onSetWindowInfo({
			type: 'delete',
			title: 'Вы уверены?',
			text: 'Вы не сможете это отменить',
			button: {
				type: 'success',
				text: 'Да, удалить',
				onClick: () =>
					onDeleteOrder({ id: activeOrderId, redirect: handleModalClose, searchQuery: query, pathname }),
			},
		});
	};

	const handleOrderFormSubmit = async () => {
		await onFormSubmit(FORM_NAME);
		if (activeOrderId) {
			onUpdateOrder({
				id: activeOrderId,
				redirect: () => handleOrderFormOpenButtonClick('cancel'),
				searchQuery: removeModalQuery(query.toString()),
				pathname,
			});
			return;
		}

		onCreateOrder({ redirect: handleModalClose, searchQuery: removeModalQuery(query.toString()) });
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleOrderFormSubmit();
		}
	};

	const isOrderFormOpen = useMemo(
		() => isOrderEditFormOpen || isOrderAddFormOpen,
		[isOrderAddFormOpen, isOrderEditFormOpen],
	);

	const rightButtons = useMemo(() => {
		return isOrderFormOpen
			? [
					{
						onClick: handleOrderFormSubmit,
						text: 'Сохранить',
						color: 'primary',
						loading: isOrderInfoSending,
						disabled: isOrderInfoLoading || isOrderInfoSending,
					},
					{
						onClick: () => handleOrderFormOpenButtonClick('cancel'),
						text: 'Отмена',
						color: 'secondary',
						disabled: isOrderInfoSending,
					},
			  ]
			: [
					...(isAvailable.update && !isAuditTabShown && orderActions?.update
						? [
								{
									onClick: () => handleOrderFormOpenButtonClick('edit'),
									text: 'Изменить',
									color: 'primary',
									disabled: isOrderInfoLoading,
								},
						  ]
						: []),
					{
						onClick: handleModalClose,
						text: 'Закрыть',
						color: 'secondary',
					},
			  ];
	}, [isOrderFormOpen, query, isAvailable.update]);

	const leftButtons = useMemo(() => {
		return !isOrderFormOpen && isAvailable.delete && !isAuditTabShown && orderActions?.destroy
			? [
					{
						onClick: handleOrderDelete,
						text: 'Удалить',
						color: 'warning',
						disabled: isOrderInfoLoading,
					},
			  ]
			: [];
	}, [isOrderFormOpen, query, isAvailable.delete]);

	const headerText = useMemo(() => {
		if (isOrderEditFormOpen || isOrderAddFormOpen) {
			return isOrderEditFormOpen ? 'Изменить заказ' : 'Создать заказ';
		}

		return orderName;
	}, [isOrderEditFormOpen, isOrderAddFormOpen, orderName]);

	const navItems = useMemo(() => {
		const tabs = [];

		if (!isOrderAddFormOpen && !isOrderEditFormOpen) {
			tabs.push({
				id: 'info',
				text: 'Информация',
				onClick: () => handleNavItemClick('info'),
				isActive: !isAuditTabShown,
			});
		}

		if (!isOrderAddFormOpen && !isOrderEditFormOpen && orderTabs.audit) {
			tabs.push({
				id: 'audit',
				text: 'Изменения',
				onClick: () => handleNavItemClick('audit'),
				isActive: isAuditTabShown,
			});
		}

		return tabs;
	}, [isOrderAddFormOpen, isOrderEditFormOpen, handleNavItemClick, isAuditTabShown]);

	const modalContent = useMemo(() => {
		if (isOrderFormOpen) {
			return <OrderFormContainer />;
		}

		return !isAuditTabShown ? <OrdersInfo /> : <Audit entityName="orders" entityId={activeOrderId} />;
	}, [isOrderFormOpen, isAuditTabShown]);

	return (
		<ModalWindow
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			headerText={headerText}
			navItems={navItems}
			modalContent={modalContent}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'order' }]}
			footerButtons={<ModalFooterButtons leftButtons={leftButtons} rightButtons={rightButtons} />}
		/>
	);
};

const withForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(OrdersModalWrapper);

const mapStateToProps = createStructuredSelector({
	initialValues: getFormInitialValuesSelector(),
	activeOrderId: getActiveOrderIdSelector(),
	isModalShown: getIsModalShownSelector(),
	isOrderEditFormOpen: getIsOrderEditFormOpenSelector(),
	isOrderAddFormOpen: getIsOrderAddFormOpenSelector(),
	orderName: getOrderNameSelector(),
	isAvailable: getIsAvailableOrdersButtonsSelector(),
	isOrderInfoLoading: getIsOrderInfoLoadingSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isOrderInfoSending: getIsOrderInfoSendingSelector(),
	orderTabs: getOrderTabsSelector(),
	orderActions: getOrderActionsSelector(),
});

const mapDispatchToProps = {
	onResetState: resetStateAction,
	onDeleteOrder: deleteOrderAction,
	onCreateOrder: createOrderAction,
	onUpdateOrder: updateOrderAction,
	onGetOrderInfo: getOrderInfoAction,
	onFormSubmit: submit,
	onSetWindowInfo: setWindowInfoAction,
	onClearForm: () => reset(FORM_NAME),
};

const OrdersModal = connect(mapStateToProps, mapDispatchToProps)(withForm);

OrdersModalWrapper.propTypes = {
	isModalShown: PropTypes.bool.isRequired,
	activeOrderId: PropTypes.string,
	isOrderEditFormOpen: PropTypes.bool.isRequired,
	isOrderAddFormOpen: PropTypes.bool.isRequired,
	orderName: PropTypes.string.isRequired,
	onResetState: PropTypes.func.isRequired,
	onDeleteOrder: PropTypes.func.isRequired,
	onCreateOrder: PropTypes.func.isRequired,
	onUpdateOrder: PropTypes.func.isRequired,
	onGetOrderInfo: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isOrderInfoLoading: PropTypes.bool.isRequired,
	onSetWindowInfo: PropTypes.func.isRequired,
	isAuditTabShown: PropTypes.bool.isRequired,
	isOrderInfoSending: PropTypes.bool.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	onClearForm: PropTypes.func.isRequired,
	orderTabs: PropTypes.shape({
		audit: PropTypes.bool,
	}).isRequired,
	orderActions: PropTypes.shape({
		update: PropTypes.bool,
		destroy: PropTypes.bool,
	}).isRequired,
};

export default OrdersModal;
